import type { IUser } from "~/server/models/user";

type InternalAccountMenuItem = {
	id: string;
	label: string;
	description?: string;
	to: string;
};

type ExternalAccountMenuItem = {
	id: string;
	label: string;
	description?: string;
	href: string;
};

export type AccountMenuItem = InternalAccountMenuItem | ExternalAccountMenuItem;

export type AccountMenuSection = {
	id: string;
	label: string;
	items: AccountMenuItem[];
};

export function getAccountMenuSections(user: IUser | null | undefined): AccountMenuSection[] {
	const isAdmin = user?.admin === true;
	const canAccessUcc = user?.access_ucc === true;

	const settingsItems: AccountMenuItem[] = [
		{
			id: "profile",
			label: "Profile & Billing",
			description: "Personal, billing, and password details",
			to: "/account/profile",
		},
		{
			id: "api",
			label: "API",
			description: "API keys and endpoint access",
			to: "/account/api",
		},
	];

	if (isAdmin) {
		settingsItems.push({
			id: "users",
			label: "Users",
			description: "Manage workspace seats and invites",
			to: "/account/users",
		});
	}

	const usageItems: AccountMenuItem[] = [
		{
			id: "credit-history",
			label: "Credit History",
			description: "Daily usage and transaction totals",
			to: "/account/credit-history",
		},
	];

	if (isAdmin) {
		usageItems.unshift({
			id: "credits",
			label: "Get Credits",
			description: "Purchase credits and configure auto top-up",
			to: "/account/credits",
		});
	}

	const integrationItems: AccountMenuItem[] = [
		{
			id: "crm",
			label: "CRM Integration",
			description: "Connect and manage CRM providers",
			to: "/account/crm",
		},
	];

	if (canAccessUcc) {
		integrationItems.push({
			id: "ucc-field-mappings",
			label: "UCC Export Fields",
			description: "Control UCC export field labels",
			to: "/account/ucc-field-mappings",
		});
	}

	return [
		{
			id: "settings",
			label: "Settings",
			items: settingsItems,
		},
		{
			id: "usage",
			label: "Usage",
			items: usageItems,
		},
		{
			id: "integrations",
			label: "Integrations",
			items: integrationItems,
		},
		{
			id: "support",
			label: "Support",
			items: [
				{
					id: "support",
					label: "Help & Support",
					description: "Get documentation and support",
					to: "/support",
				},
				{
					id: "logout",
					label: "Log out",
					description: "Sign out of this session",
					href: "/auth/logout",
				},
			],
		},
	];
}
